import { useQuery } from '@tanstack/react-query';
import type {
  Account,
  ArrearsRow,
  Budget,
  ContributionCall,
  FiscalYear,
  Fund,
  FundSummary,
  JournalEntry,
  UnitStatement,
} from '@syndiqia/ledger/contracts';
import type { Ownership } from '@syndiqia/registry/contracts';
import { ApiError, useApi } from './api';
import { useRegistryMutation } from './queries';
import { useSyndicate } from './session';

// Query keys start with [tenant, 'ledger'] so one invalidation refreshes every financial view after a write.

function useLedgerQuery<T>(key: unknown[], path: string, options: { enabled?: boolean; allow404?: boolean } = {}) {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'ledger', ...key],
    queryFn: async () => {
      try {
        return await api<T>(`${s.api}/ledger${path}`);
      } catch (e) {
        if (options.allow404 && e instanceof ApiError && e.status === 404) return null as T; // only for T that includes null
        throw e;
      }
    },
    enabled: options.enabled ?? true,
  });
}

export const useFiscalYears = () => useLedgerQuery<FiscalYear[]>(['fiscal-years'], '/fiscal-years');
export const useFunds = () => useLedgerQuery<Fund[]>(['funds'], '/funds');
export const useAccounts = () => useLedgerQuery<Account[]>(['accounts'], '/accounts');
export const useBudget = (yearId: string | undefined) =>
  useLedgerQuery<Budget | null>(['budget', yearId], `/fiscal-years/${yearId}/budget`, { enabled: !!yearId, allow404: true });
export const useCalls = (yearId: string | undefined) =>
  useLedgerQuery<ContributionCall[]>(['calls', yearId], `/fiscal-years/${yearId}/contribution-calls`, { enabled: !!yearId });
export const useFundSummaries = (enabled: boolean) => useLedgerQuery<FundSummary[]>(['fund-summaries'], '/fund-summaries', { enabled });
export const useArrears = (enabled: boolean) => useLedgerQuery<ArrearsRow[]>(['arrears'], '/arrears', { enabled });
export const useDueToFunds = (enabled: boolean) =>
  useLedgerQuery<{ fundCode: string; amountCents: number }[]>(['due-to-funds'], '/due-to-funds', { enabled });
export const useJournal = () => useLedgerQuery<JournalEntry[]>(['journal'], '/journal?limit=300');
export const useStatement = (unitId: string, enabled: boolean) =>
  useLedgerQuery<UnitStatement>(['statement', unitId], `/units/${unitId}/statement`, { enabled });

export function useAllOwnership() {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'ownership', 'all'],
    queryFn: () => api<Ownership[]>(`${s.api}/registry/ownership`),
    enabled: s.can('registry.contacts.read'),
  });
}

/** The fiscal year containing today, else the most recent one. */
export function useCurrentYear() {
  const years = useFiscalYears();
  const today = new Date().toISOString().slice(0, 10);
  const year = years.data?.find((y) => y.start <= today && today < y.end) ?? years.data?.[0];
  return { ...years, year };
}

/** A write to the Ledger API; refreshes every financial view. */
export function useLedgerMutation<Input, Output>(request: (api: ReturnType<typeof useApi>, base: string, input: Input) => Promise<Output>) {
  return useRegistryMutation<Input, Output>((api, registryBase, input) => request(api, registryBase.replace(/\/registry$/, '/ledger'), input), [
    'ledger',
  ]);
}
